import { useCallback, useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { uniqueBy } from 'lib/array/arrayUtils'
import { searchHotels } from 'tripPlanner/api'
import * as HotelSearchKeys from 'tripPlanner/hooks/reactQueryKeys/hotelSearch'
import { mapToDrawerHotel } from './mapToDrawerHotel'
import type { HotelSearchSection } from 'tripPlanner/api/types'
import type { DrawerHotel } from './types'

const EMPTY_HOTELS: Array<DrawerHotel> = []
const EMPTY_SECTIONS: Array<HotelSearchSection> = []

const HOTEL_SEARCH_STALE_TIME = 5 * 60 * 1000

const INITIAL_LIMIT = 9
const LOAD_MORE_LIMIT = 5

interface HotelSearchPageParam {
  loadedCount: number
}

interface UseHotelSearchOptions {
  searchQuery: string
  placeId?: string
  region?: string
  checkIn?: string
  checkOut?: string
  flexibleMonths?: string
  flexibleNights?: string
  occupancies?: Array<App.Occupants>
  enabled: boolean
}

export default function useHotelSearch({
  searchQuery,
  placeId,
  region,
  checkIn,
  checkOut,
  flexibleMonths,
  flexibleNights,
  occupancies,
  enabled,
}: UseHotelSearchOptions) {
  const queryResult = useInfiniteQuery({
    queryKey: HotelSearchKeys.search(searchQuery, region, checkIn, checkOut, flexibleMonths, flexibleNights, placeId, occupancies),
    queryFn: ({ pageParam }) => {
      const continuation = pageParam as HotelSearchPageParam | undefined
      const limit = continuation ? LOAD_MORE_LIMIT : INITIAL_LIMIT
      return searchHotels({
        query: searchQuery,
        placeId,
        region,
        checkIn,
        checkOut,
        flexibleMonths,
        flexibleNights,
        occupancies,
        loadedCount: continuation?.loadedCount,
        limit,
      })
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore) return undefined

      return {
        loadedCount: lastPage.loadedCount,
      }
    },
    enabled: enabled && !!searchQuery,
    staleTime: HOTEL_SEARCH_STALE_TIME,
  })

  const searchResults = useMemo(() => {
    if (!queryResult.data?.pages) {
      return {
        hotels: EMPTY_HOTELS,
        sections: EMPTY_SECTIONS,
      }
    }

    const visibleHotels: Array<DrawerHotel> = []
    const seenHotelOfferIds = new Set<string>()
    const visibleSections: Array<HotelSearchSection> = []

    queryResult.data.pages.forEach((page) => {
      const pageVisiblePositions: Array<number> = []
      const pageStartPosition = visibleHotels.length

      page.items.forEach((item, index) => {
        const hotel = mapToDrawerHotel(item)
        if (hotel && !seenHotelOfferIds.has(hotel.offerId)) {
          seenHotelOfferIds.add(hotel.offerId)
          visibleHotels.push(hotel)
          pageVisiblePositions.push(index)
        }
      })

      const pageSections = [...page.sections].sort((a, b) => a.position - b.position)

      pageSections.forEach((section) => {
        visibleSections.push({
          ...section,
          position: pageStartPosition + pageVisiblePositions.filter((position) => position < section.position).length,
        })
      })
    })

    const dedupedSections = uniqueBy(
      visibleSections,
      (section) => `${section.type}:${section.position}`,
    )

    return {
      hotels: visibleHotels,
      sections: dedupedSections,
    }
  }, [queryResult.data])

  const loadMore = useCallback(() => {
    if (queryResult.hasNextPage && !queryResult.isFetchingNextPage) {
      queryResult.fetchNextPage()
    }
  }, [queryResult])

  return {
    hotels: searchResults.hotels,
    sections: searchResults.sections,
    isLoading: queryResult.isLoading && queryResult.fetchStatus !== 'idle',
    isError: queryResult.isError,
    hasMore: !!queryResult.hasNextPage,
    isLoadingMore: queryResult.isFetchingNextPage,
    loadMore,
    refetch: queryResult.refetch,
  }
}
